import sumBy from 'lodash/sumBy';

import {AudienceCategoryValue, Quota} from 'client/common/types';

export class ParseQuotasError extends Error {
    name: 'ParseQuotasError';
    constructor(public reason?: string) {
        super();
        this.name = 'ParseQuotasError';
        this.reason = reason;
    }
}

export const GENDERS = ['0', '1'] as const;
export const AGES = ['1', '2', '3', '4', '5'] as const;

const ALLOWED_CATEGORY_IDS = ['age', 'gender'] as const;

type AllowedCategoryId = typeof ALLOWED_CATEGORY_IDS[number];

export type ParsedQuota = {
    [key in AllowedCategoryId]?: AudienceCategoryValue;
} & {
    sizePerc: number;
};

export function parseQuotas(quotas: Quota[]) {
    try {
        // eslint-disable-next-line max-statements
        return quotas.reduce((acc, quota) => {
            if (quota.type !== 'RESP_PARAMS') {
                throw new ParseQuotasError(`Quota type ${quota.type} is unsupported`);
            }

            const parsedQuota = {
                sizePerc: Math.floor(quota.sizePerc)
            } as ParsedQuota;

            for (const category of quota.categories) {
                const {categoryId} = category;
                let {value} = category;

                if (categoryId === 'geo') {
                    continue;
                }

                if (Array.isArray(value)) {
                    // eslint-disable-next-line max-depth
                    if (value.length > 1) {
                        throw new ParseQuotasError('Category value is not exact');
                    }

                    // @ts-ignore
                    [value] = value;
                }

                // @ts-ignore
                if (parsedQuota[categoryId]) {
                    throw new ParseQuotasError('Category value is not exact');
                }

                // @ts-ignore
                parsedQuota[categoryId] = value;
            }

            acc.push(parsedQuota);

            return acc;
        }, [] as ParsedQuota[]);
    } catch (e) {
        if (e instanceof ParseQuotasError) {
            return [];
        }

        throw e;
    }
}

export type QuotasType = 'all' | 'gender' | 'age';

export function getQuotasType(parsedQuotas?: ParsedQuota[]): QuotasType | undefined {
    let type: QuotasType;

    if (!parsedQuotas || parsedQuotas.length === 0) {
        return;
    }

    if ('age' in parsedQuotas[0]) {
        type = 'gender' in parsedQuotas[0] ? 'all' : 'age';
    } else {
        type = 'gender';
    }

    const isValidQuotas = parsedQuotas.every(quota => {
        if (type === 'age') {
            return 'age' in quota;
        }

        if (type === 'gender') {
            return 'gender' in quota;
        }

        return 'age' in quota && 'gender' in quota;
    });

    return isValidQuotas ? type : undefined;
}

export function genRequestQuotas(quotas: ParsedQuota[]) {
    return quotas.map(quota => {
        const {age, gender, sizePerc} = quota;

        const categories = [];

        if ('age' in quota) {
            categories.push({
                categoryId: 'age',
                value: [age]
            });
        }

        if ('gender' in quota) {
            categories.push({
                categoryId: 'gender',
                value: [gender]
            });
        }

        return {
            type: 'RESP_PARAMS',
            sizePerc,
            categories,
            segments: [],
            interests: []
        } as Quota;
    });
}

export function getPin(index: number, len: number): 'brick-brick' | 'round-brick' | 'brick-round' {
    if (index === 0) {
        return 'round-brick';
    }

    if (index === len - 1) {
        return 'brick-round';
    }

    return 'brick-brick';
}

export const DEFAULT_ALL_QUOTAS: ParsedQuota[] = [
    {
        gender: '0',
        age: '1',
        sizePerc: 6
    },
    {
        gender: '0',
        age: '2',
        sizePerc: 18
    },
    {
        gender: '0',
        age: '3',
        sizePerc: 12
    },
    {
        gender: '0',
        age: '4',
        sizePerc: 8
    },
    {
        gender: '0',
        age: '5',
        sizePerc: 6
    },
    {
        gender: '1',
        age: '1',
        sizePerc: 6
    },
    {
        gender: '1',
        age: '2',
        sizePerc: 17
    },
    {
        gender: '1',
        age: '3',
        sizePerc: 12
    },
    {
        gender: '1',
        age: '4',
        sizePerc: 7
    },
    {
        gender: '1',
        age: '5',
        sizePerc: 8
    }
];

export const DEFAULT_GENDER_QUOTAS: ParsedQuota[] = GENDERS.map(gender => ({
    gender,
    sizePerc: sumBy(DEFAULT_ALL_QUOTAS, quota => (quota.gender === gender ? quota.sizePerc : 0))
}));

export const DEFAULT_AGE_QUOTAS: ParsedQuota[] = AGES.map(age => ({
    age,
    sizePerc: sumBy(DEFAULT_ALL_QUOTAS, quota => (quota.age === age ? quota.sizePerc : 0))
}));
